import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Progress,
  Text,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import type { SkillUpliftResult } from '../hooks/useSkillPayUplift';
import type { UpliftMode } from '../IndividualPotentialPage';
import { MissingSkillCard } from './MissingSkillCard';

interface MissingSkillsPanelProps {
  skills: SkillUpliftResult[];
  userSalary: number;
  isLoading: boolean;
  progress: number;
  upliftMode: UpliftMode;
}

export const MissingSkillsPanel = ({
  skills,
  userSalary,
  isLoading,
  progress,
  upliftMode,
}: MissingSkillsPanelProps) => {
  // Split skills into positive uplift vs negative/no uplift
  const { highValueSkills, lowerPrioritySkills } = useMemo(() => {
    const highValue: SkillUpliftResult[] = [];
    const lowerPriority: SkillUpliftResult[] = [];

    for (const skill of skills) {
      if (skill.upliftPercent !== null && skill.upliftPercent > 0) {
        highValue.push(skill);
      } else {
        lowerPriority.push(skill);
      }
    }

    // Sort high value by uplift desc
    highValue.sort((a, b) => (b.upliftPercent ?? 0) - (a.upliftPercent ?? 0));

    // Sort lower priority: those with data first (by role share), then no data
    lowerPriority.sort((a, b) => {
      if (a.upliftPercent !== null && b.upliftPercent === null) return -1;
      if (a.upliftPercent === null && b.upliftPercent !== null) return 1;
      return b.roleShare - a.roleShare;
    });

    return { highValueSkills: highValue, lowerPrioritySkills: lowerPriority };
  }, [skills]);

  if (skills.length === 0 && !isLoading) {
    return (
      <Box>
        <Heading size="xs" color="gray.600" mb={3}>
          Top Missing Skills for Pay Growth
        </Heading>
        <Text fontSize="sm" color="green.500">
          You have all the top skills for this role!
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack mb={3} justify="space-between" align="baseline">
        <Heading size="xs" color="gray.600">
          Top Missing Skills for Pay Growth
        </Heading>
        {isLoading && (
          <Text fontSize="xs" color="gray.400">
            Analyzing {Math.round(progress * 100)}%
          </Text>
        )}
      </HStack>

      {isLoading && (
        <Progress
          value={progress * 100}
          size="xs"
          colorScheme="blue"
          borderRadius="full"
          mb={4}
        />
      )}

      {/* High-value skills with positive pay uplift */}
      {highValueSkills.length > 0 && (
        <Box mb={lowerPrioritySkills.length > 0 ? 5 : 0}>
          <Text fontSize="xs" color="green.600" fontWeight="medium" mb={2}>
            Skills with positive pay uplift
          </Text>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            {highValueSkills.map((skill) => (
              <GridItem key={skill.skillId}>
                <MissingSkillCard
                  skillName={skill.skillName}
                  roleShare={skill.roleShare}
                  upliftPercent={skill.upliftPercent}
                  upliftDollar={skill.upliftDollar}
                  trend={skill.trend}
                  trendChangePercent={skill.trendChangePercent}
                  timeseries={skill.timeseries}
                  targetSalaryWithSkill={skill.targetSalaryWithSkill}
                  upliftMode={upliftMode}
                  userSalary={userSalary}
                />
              </GridItem>
            ))}
          </Grid>
        </Box>
      )}

      {/* Lower priority skills (negative or no uplift data) */}
      {lowerPrioritySkills.length > 0 && (
        <Box>
          <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={2}>
            {highValueSkills.length > 0
              ? 'Lower priority — no clear pay benefit'
              : 'No skills found with positive pay uplift'}
          </Text>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            {lowerPrioritySkills.map((skill) => (
              <GridItem key={skill.skillId}>
                <MissingSkillCard
                  skillName={skill.skillName}
                  roleShare={skill.roleShare}
                  upliftPercent={skill.upliftPercent}
                  upliftDollar={skill.upliftDollar}
                  trend={skill.trend}
                  trendChangePercent={skill.trendChangePercent}
                  timeseries={skill.timeseries}
                  targetSalaryWithSkill={skill.targetSalaryWithSkill}
                  upliftMode={upliftMode}
                  userSalary={userSalary}
                  isLowerPriority
                />
              </GridItem>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};
